import { FC, PropsWithChildren, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Box, BoxProps, Button, ButtonProps, SxProps } from '@mui/material'
import { keyframes } from '@emotion/react'
import {
    IndicatorIcon,
    LeftArrowIcon,
    MessagesIcon,
    ProfileIcon
} from 'src/components/MUIBased/icons'

import { useLocation } from 'react-router'
import { useAmpli } from 'src/hooks'

interface HeaderIndicatorButtonProps {
    color: string
    showIndicator: boolean
    sx?: SxProps
}

export const HeaderYourCareButton = ({
    color,
    showIndicator
}: HeaderIndicatorButtonProps) => {
    return (
        <Link to="/your-care">
            <Button variant="header" sx={{ color: `${color} !important` }}>
                <Box
                    sx={{
                        width: '32px',
                        height: '24px',
                        textAlign: 'left',
                        position: 'relative',
                        mr: '4px',
                        lineHeight: 0
                    }}
                >
                    <ProfileIcon color={color} />
                    {showIndicator && <IndicatorIcon sx={{ right: '6px' }} />}
                </Box>
                Your Care
            </Button>
        </Link>
    )
}

export const HeaderChatButton = ({
    color,
    showIndicator
}: HeaderIndicatorButtonProps) => {
    return (
        <Link to="/chat">
            <Button variant="header" sx={{ color: `${color} !important` }}>
                <Box
                    sx={{
                        width: '32px',
                        height: '24px',
                        textAlign: 'left',
                        position: 'relative',
                        mr: '8px',
                        lineHeight: 0
                    }}
                >
                    <MessagesIcon color={color} />
                    {showIndicator && <IndicatorIcon />}
                </Box>
                Your Messages
            </Button>
        </Link>
    )
}

export const HeaderBookAppointmentButton = () => {
    return (
        <Link to="/appointments">
            <Button
                color="primary"
                variant="headerContained"
                sx={{ p: '6px 24px' }}
            >
                Book appointment
            </Button>
        </Link>
    )
}

type BackButtonProps = {
    sx?: SxProps
    href?: string
    variant?: 'default' | 'white'
}

export const BackButton: FC<PropsWithChildren<BackButtonProps>> = ({
    href,
    sx,
    variant,
    children = 'Back'
}) => {
    const searchParams = new URLSearchParams(window.location.search)
    const location = useLocation()
    const { backButtonClicked } = useAmpli()
    const color = variant === 'white' ? '#ffffff' : '#282725'
    const backTo = searchParams.get('backTo')

    return (
        <Link
            onClick={() => {
                backButtonClicked(location.pathname)
            }}
            //@ts-ignore TS gets confused
            to={backTo ? backTo : href || -1} // -1 goes back in history :D
        >
            <Button
                sx={{
                    'display': 'flex',
                    'gap': '4px',
                    'fontSize': '12px',
                    'fontWeight': 'normal',
                    ':hover': {
                        fontWeight: 'bold'
                    },
                    'textTransform': 'none !important',
                    color,
                    ...sx
                }}
            >
                <LeftArrowIcon color={color} />
                {children}
            </Button>
        </Link>
    )
}

const animation1 = keyframes({
    from: {
        transform: 'scale(0)'
    },
    to: {
        transform: 'scale(1)'
    }
})

const animation3 = keyframes({
    from: {
        transform: 'scale(1)'
    },
    to: {
        transform: 'scale(0)'
    }
})

const animation2 = keyframes({
    from: {
        transform: 'translate(0, 0)'
    },
    to: {
        transform: 'translate(14px, 0)'
    }
})

const Dot = (props: BoxProps) => (
    <Box
        sx={{
            position: 'absolute',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            animationTimingFunction: 'cubic-bezier(0, 1, 1, 0)',
            ...props.sx
        }}
    />
)

const DotLoader = (props: BoxProps) => {
    return (
        <Box
            {...props}
            role="img"
            sx={{
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...props.sx
            }}
        >
            <Box
                sx={{
                    width: 7,
                    height: 2,
                    position: 'relative'
                }}
            >
                <Dot
                    sx={{
                        left: '4px',
                        animation: `${animation1} 0.6s infinite`
                    }}
                    color={(props.color as string) || 'primary'}
                />
                <Dot
                    sx={{
                        left: '4px',
                        animation: `${animation2} 0.6s infinite`
                    }}
                    color={(props.color as string) || 'primary'}
                />
                <Dot
                    sx={{
                        left: '19px',
                        animation: `${animation2} 0.6s infinite`
                    }}
                    color={(props.color as string) || 'primary'}
                />
                <Dot
                    sx={{
                        left: '33px',
                        animation: `${animation3} 0.6s infinite`
                    }}
                    color={(props.color as string) || 'primary'}
                />
            </Box>
        </Box>
    )
}

interface LoadingButtonProps extends ButtonProps {
    isLoading?: boolean
    variant?: 'contained' | 'outlined' | 'text' | 'header' | 'headerContained'
    children: ReactNode
}

export const LoadingButton: FC<LoadingButtonProps> = specs => {
    const {
        isLoading = false,
        variant = 'contained',
        children,
        ...props
    } = specs

    return (
        <Button
            variant={variant}
            disabled={isLoading || props.disabled}
            {...props}
        >
            {isLoading && <DotLoader />}
            {children}
        </Button>
    )
}
